import { zodResolver } from '@hookform/resolvers/zod';
import { getAuth } from 'firebase/auth';
import { collection, doc, getDoc, getDocs, getFirestore, query, setDoc, where } from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { Check, Eye, RefreshCcw, Upload, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';
import Header from '../components/layout/Header';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { app, storage } from "../firebase";
import { useToast } from '../hooks/use-toast';


// Define Zod schema for form validation  
const certificateSchema = z.object({
  batchId: z.string().nonempty("Batch ID is required"),
  batchName: z.string().optional(),
  certificateId: z.string().nonempty("Certificate ID is required"),
  certificateName: z.string().nonempty("Certificate Name is required"),
  courseDescription: z.string().optional(),
  courseId: z.string().optional(),
  courseName: z.string().nonempty("Course Name is required"),
  duration: z.string().optional(),
  emailId: z.string().email("Invalid email address").optional(),
  fromDate: z.string().nonempty("From Date is required"),
  gender: z.string().nonempty("Gender is required"),
  issuedDate: z.any().optional(),
  mobileNumber: z.string().nonempty("Mobile Number is required"),
  studentIdNo: z.string().nonempty("Roll Number is required"),
  status: z.string().nonempty("Status is required"),
  studentsName: z.string().nonempty("Student's Name is required"),
  toDate: z.string().nonempty("To Date is required"),
  certificate: z.any().refine(val => !!val, {
    message: "Certificate is required.",
  }),
});

// Types for the form  
type CertificateForm = z.infer<typeof certificateSchema>;

const GenerateCertificate = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
    const [viewCertificate, setViewCertificate] = useState(null);

  const db = getFirestore(app);
  const auth = getAuth(app);

  // Initialize the form with React Hook Form + Zod validation  
  const { register, handleSubmit, formState: { errors, isValid }, setValue, reset, getValues } = useForm<CertificateForm>({
    resolver: zodResolver(certificateSchema),
    defaultValues: {
      batchId: '',
      batchName: '',
      certificateId: '', // Generate initial ID  
      certificateName: '',
      courseDescription: '',
      courseId: '',
      courseName: '',
      duration: '',
      emailId: '',
      fromDate: '',
      gender: '',
      issuedDate: '',
      mobileNumber: '',
      studentIdNo: '',
      status: 'Issued',
      studentsName: '',
      toDate: '',
      certificate: null
    },
    mode: "onChange",
  });

  // Fetch student data from Firestore  
  useEffect(() => {
    const fetchStudentData = async () => {
      if (id) {
        const docRef = doc(db, 'certifiedStudents', id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          // Populate the form with the fetched data  
          reset({
            ...data,
            batchId: data?.batchId,
            batchName: data?.batchName,
            certificateName: data?.certificateName,
            courseName: data?.courseName,
            emailId: data?.emailId,
            gender: data?.gender,
            mobileNumber: data?.mobileNo ?? data?.mobileNumber,
            studentIdNo: data?.rollNo ?? data?.studentIdNo,
            studentsName: data?.studentsName ?? '',
            issuedDate: data?.issuedDate ? new Date(data?.issuedDate?.seconds * 1000) : new Date(),

          });
        } else {
          toast({ title: "Error", description: "No such student found!", variant: "destructive" });
        }
      }
    };

    fetchStudentData();
  }, [id, db, reset, toast]);

  const handleRollNoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rollNo = e.target.value;
    setValue('studentIdNo', rollNo); // Set the roll number in the form  
    if (rollNo) {
      fetchStudentByRollNo(rollNo); // Fetch student data    
    }
  };

  // Function to fetch student data by Roll No  
  const fetchStudentByRollNo = async (rollNo: string) => {
    try {
      const studentsRef = collection(db, 'students');
      const q = query(studentsRef, where('studentIdNo', '==', rollNo));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const data = querySnapshot.docs[0].data();
        // Populate form fields with the retrieved student data  
        reset({
          batchId: data?.batchIds[0] || '',
          batchName: data?.batchName || '',
          certificateId: data?.certificateId || '',
          certificateName: data?.certificateName || '',
          courseDescription: data?.courseDescription || '',
          courseId: data?.courseId || '',
          courseName: data?.courseName || '',
          duration: data?.duration || '',
          emailId: data?.emailId || '',
          fromDate: data?.fromDate || '',
          gender: data?.gender || '',
          issuedDate: data?.issuedDate || '',
          mobileNumber: data?.mobileNumber || '',
          studentIdNo: data?.studentIdNo || '',
          status: 'Issued',
          studentsName: data?.studentsName || '',
          toDate: data?.toDate || '',
          certificate: data?.certificate ?? null
        });
        toast({
          title: "Success",
          description: "Student data loaded successfully.",
          variant: "default"
        });
      } else {
        toast({
          title: "Error",
          description: "No student found with this Roll Number.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error("Error fetching student data: ", error);
      toast({
        title: "Error",
        description: "An error occurred while fetching student data.",
        variant: "destructive"
      });
    }
  };

  // Function to handle file selection and uploading  
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    e.stopPropagation();

    setLoading(true);
    const file = e.target.files?.[0];
    if (!file) return; // Exit if no file was selected   

    // Prepare the final certificate material object with the URL from storage  
    let certificateObj = {
      fileName: `${Date.now().toString()}_${file.name}`,
      size: file.size,
      url: '' // Set the URL after the file is uploaded  
    };

    // Prepare for uploading the file to storage  
    const storageRef = ref(storage, `files/${certificateObj?.fileName}`);
    try {
      // Upload the file to storage  
      await uploadBytes(storageRef, file);

      // Get the download URL after successful upload  
      const url = await getDownloadURL(storageRef);

      // Prepare the final certificate material object with the URL from storage  

      certificateObj.url = url // Set the URL after the file is uploaded  


      // Update the form's field and state with the new object  
      setValue('certificate', certificateObj);
      reset({ ...getValues(), certificate: certificateObj });
    } catch (uploadError) {
      console.error(uploadError);
      toast({
        title: "Error",
        description: uploadError.message || "Error uploading syllabus material",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const onSubmit: SubmitHandler<CertificateForm> = async (data) => {
    setLoading(true);
    const certificateData = {
      ...data,
      issuedDate: new Date(data?.issuedDate as any),
      certificateUrl: data?.certificate?.url
    };
    try {
      if (id) {
        // Update the existing document in Firestore  
        const courseRef = doc(db, "certifiedStudents", id);
        await setDoc(courseRef, certificateData); // Update the course data in Firestore  
        toast({
          title: "Certificate updated",
          description: `Certificate has been successfully updated`,
        });
      } else {
        // Create a reference to a new document in the Firestore certificates collection  
        const newCourseRef = doc(collection(db, "certifiedStudents"));

        // Add the generated ID to the course data object  
        const courseDataWithId = {
          id: newCourseRef.id,
          ...certificateData,
          issuedDate: new Date(),

        };

        // Save the course data to Firestore with the generated ID  
        await setDoc(newCourseRef, courseDataWithId);
        toast({
          title: "Certificate created",
          description: `Certificate has been successfully generated`,
        });
      }
      navigate('/certificates');
    } catch (error) {
      console.error("Error creating/updating certificate:", error);
      toast({
        title: "Error",
        description: "Failed to create or update the certificate. Please try again.",
        variant: "destructive"
      });
      setLoading(false);
    } finally {
      setLoading(false);
      // navigate('/certificates');  
    }
  };

  const handlePreview = () => {
    setPreviewMode(true);
    setViewCertificate({...getValues()})
  };

  // Function to generate a unique certificate ID  
  function generateInitialCertificateId() {
    return `BS${generateRandomString(10)}${getDateSuffix()}`;
  }

  function generateRandomString(length: number) {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = length; i > 0; --i) {
      result += chars[Math.floor(Math.random() * chars.length)];
    }
    return result;
  }

  function getDateSuffix() {
    const today = new Date();
    const day = today.getDate().toString().padStart(2, '0');
    const month = (today.getMonth() + 1).toString().padStart(2, '0');
    const year = today.getFullYear().toString().substring(2);
    return `${day}${month}${year}`;
  }

  return (
    <div className="h-full flex flex-col bg-gray-50">
      <Header title="Generate New Certificate" subtitle="Create certification for students" />

      <div className="flex-1 p-8 animate-fade-in">
        <Card className="max-w-4xl mx-auto">
          <CardContent className="p-6">
            <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
              <div className="space-y-4">
                <h3 className="text-lg font-medium">Student Information</h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="studentIdNo">Roll No*</Label>
                    <Input
                      id="studentIdNo"
                      placeholder="Enter Roll No..."
                      className="w-full"
                      {...register('studentIdNo')}
                      onChange={handleRollNoChange}
                    />
                    {errors.studentIdNo && <p className="text-red-500 text-sm">{errors.studentIdNo.message}</p>}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="studentsName">Student Name*</Label>
                    <Input
                      id="studentsName"
                      placeholder="Enter name..."
                      className="w-full"
                      {...register('studentsName')}
                    />
                    {errors.studentsName && <p className="text-red-500 text-sm">{errors.studentsName.message}</p>}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="emailId">Email ID</Label>
                    <Input
                      id="emailId"
                      type="email"
                      placeholder="Enter email..."
                      className="w-full"
                      {...register('emailId')}
                    />
                    {errors.emailId && <p className="text-red-500 text-sm">{errors.emailId.message}</p>}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="mobileNumber">Mobile Number*</Label>
                    <Input
                      id="mobileNumber"
                      placeholder="Enter mobile number..."
                      className="w-full"
                      {...register('mobileNumber')}
                    />
                    {errors.mobileNumber && <p className="text-red-500 text-sm">{errors.mobileNumber.message}</p>}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="courseName">Course Name*</Label>
                    <Input
                      {...register('courseName')}
                      id="courseName"
                      placeholder="Enter course name..."
                      className="w-full"
                    />
                    {errors.courseName && <p className="text-red-600">{errors.courseName.message}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="certificateName">Certificate Name*</Label>
                    <Input
                      id="certificateName"
                      placeholder="Enter certificate name..."
                      className="w-full"
                      {...register('certificateName')}
                    />
                    {errors.certificateName && <p className="text-red-500 text-sm">{errors.certificateName.message}</p>}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="certificateId">Certificate ID*</Label>
                  <div className="flex gap-2">
                    <Input
                      id="certificateId"
                      placeholder="Certificate ID"
                      className="w-full"
                      disabled={true}
                      {...register('certificateId')}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setValue('certificateId', generateInitialCertificateId())}
                    >
                      <RefreshCcw size={16} className="mr-2" />
                      Generate
                    </Button>
                  </div>
                  {errors.certificateId && <p className="text-red-500 text-sm">{errors.certificateId.message}</p>}
                </div>



                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="fromDate">From Date*</Label>
                    <Input
                      {...register('fromDate')}
                      id="fromDate"
                      type="date"
                      className="w-full"
                    />
                    {errors.fromDate && <p className="text-red-600">{errors.fromDate.message}</p>}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="toDate">To Date*</Label>
                    <Input
                      {...register('toDate')}
                      id="toDate"
                      type="date"
                      className="w-full"
                    />
                    {errors.toDate && <p className="text-red-600">{errors.toDate.message}</p>}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="status">Status*</Label>
                  <Select
                    onValueChange={(value) => setValue('status', value)}
                    defaultValue={getValues('status')} // Maintain controlled input  
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select status..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Issued">Issued</SelectItem>
                      <SelectItem value="Pending">Pending</SelectItem>
                      <SelectItem value="Canceled">Canceled</SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.status && <p className="text-red-500 text-sm">{errors.status.message}</p>}
                </div>

                <div className="space-y-2">
                  <Label>Upload Certificate*</Label>
                  <div className="border border-dashed border-gray-300 rounded-md p-6 text-center">
                    <div className="space-y-2">
                      <div className="mx-auto w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                        <Upload size={24} className="text-gray-500" />
                      </div>
                      <div className="text-sm text-gray-600">
                        <p>Drag and drop an image here, or click to select an image</p>
                        <p className="text-xs text-gray-500">PNG, JPG up to 5MB</p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        type="button"
                        className="relative mt-2"
                        onClick={(e) => {
                          document.getElementById('certificate-upload')?.click();
                        }}
                      >
                        Choose File
                        <input
                          id="certificate-upload"
                          type="file"
                          className="hidden"
                          onChange={handleFileChange} // Trigger file upload  
                        />
                      </Button>
                      {getValues('certificate') && (
                        <p className="text-xs text-green-600 flex items-center justify-center gap-1 mt-1">
                          <Check size={14} /> {getValues('certificate').fileName}
                        </p>
                      )}
                    </div>
                  </div>
                  {errors.certificate && <p className="text-red-500 text-sm">Certificate is required</p>}
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handlePreview}
                  disabled={!getValues('certificate')?.url}
                >
                  <Eye size={18} className="mr-2" />
                  Preview
                </Button>
                <Button
                  type="submit"
                  disabled={loading || !isValid}
                  className="bg-bytesplash-blue hover:bg-bytesplash-blue/90"
                >
                  {loading ? "Processing..." : !!id ? 'Uupdate' : "Generate Certificate"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {previewMode && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white p-4 rounded-lg max-w-3xl w-full h-[95vh] overflow-auto">
              <div className="flex justify-between mb-1">
                <h2 className="text-xl font-bold text-primary">Certificate Preview</h2>
                <Button variant="ghost" onClick={() => setPreviewMode(false)}>
                  <X size={24} /> {/* Use the X icon for closing */}

                </Button>
              </div>
              <div className="border p-0 rounded-lg">
                <div className="text-center space-y-0">
                  {/* Check if the certificate URL exists */}
                  <div className="text-center space-y-0">
                    {/* Check if the certificate URL exists */}
                    {viewCertificate['certificate']?.url && (
                      <div className="my-4">
                        {/* Check the file type based on the URL extension */}
                        {viewCertificate['certificate'].url.includes('.pdf') ? (
                          // Display PDF if the URL ends with .pdf  
                          <embed
                            src={viewCertificate['certificate'].url}
                            type="application/pdf"
                            width="100%"
                            height="500px"
                            className="border border-gray-200 rounded-lg"
                            aria-label="Uploaded Certificate PDF"
                          />
                        ) : (
                          // Display image if not a PDF  
                          <img
                            src={viewCertificate['certificate'].url}
                            alt="Uploaded Certificate"
                            className="max-w-full h-auto border border-gray-200 rounded-lg"
                          />
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GenerateCertificate;  